import { type VariantProps, cva } from "class-variance-authority";
import Link, { type LinkProps } from "next/link";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";

import { uiOutlineClass } from "@/components/ui/global/outline";

const iconButton = cva(
  ["flex w-fit cursor-pointer items-center justify-center rounded-button-rounding", uiOutlineClass],
  {
    variants: {
      size: {
        small: "p-height-s",
        medium: "p-height-m",
        large: "p-height-l",
      },
      variant: {
        primary: [
          "bg-secondary-500",
          "text-secondary-50",
          "hover:bg-secondary-200",
          "active:bg-secondary-100",
          "focus-visible:outline-secondary-200",
          "disabled:bg-neutral-75",
        ],
        secondary: [
          "bg-secondary-200",
          "text-secondary-50",
          "hover:bg-secondary-500",
          "active:bg-secondary-100",
          "focus-visible:outline-primary",
          "disabled:bg-neutral-75",
        ],
        outlined: [
          "border",
          "border-secondary-200",
          "text-secondary-400",
          "hover:border-secondary-100",
          "hover:text-secondary-100",
          "active:border-secondary-500",
          "active:text-secondary-500",
          "focus-visible:outline-secondary-200",
          "disabled:border-secondary-75",
          "disabled:text-secondary-75",
        ],
        transparent: [
          "bg-transparent",
          "text-secondary-50",
          "hover:bg-transparent",
          "active:bg-transparent",
          "focus-visible:outline-secondary-200",
        ],
      },
    },
    defaultVariants: {
      size: "medium",
      variant: "primary",
    },
  },
);

type IconButtonVariantProps = VariantProps<typeof iconButton>;

type IconButtonProps = IconButtonVariantProps &
  Omit<AriaButtonProps, "children"> & {
    label: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    link?: LinkProps & { target?: string };
    iconClassName?: string;
  };

export const IconButton = ({
  label,
  icon: Icon,
  variant,
  size,
  link,
  iconClassName = "h-6 w-6",
  ...props
}: IconButtonProps) => {
  const Component: any = link ? Link : AriaButton;

  return (
    <Component
      {...props}
      {...link}
      isDisabled={props.isDisabled}
      className={iconButton({ variant, size, className: props.className })}
      aria-label={label}
    >
      <Icon className={iconClassName} />
    </Component>
  );
};
